import React, { Component } from "react";
import PropTypes from "prop-types";
import { Tabs, Tab } from "react-bootstrap";
import { hasAccess } from "../util/AccessControlUtil";
import { PathHelper } from '../util/UrlUtil';
import { pickDataAttributes } from "../Util";

/**
 * This component is used to create Tabs based on the routing in the index.js of a bundle.
 * It requires a few props to be set on each Route:  title, routeKey, path, and the Component being rendered.
 * These Routes can be set directly or mapped through as an array of tab props to pass down for example:
 * const tabList = [
  *  {
  *    path: "compliance",
  *    routeKey: 'corporate-compliance',
  *    title: 'Conduct & Culture',
  *    component: TeamsContainer
  *  }]
  * and in the index.js:
  * {tabList.map((tab, index) => (<Route keys={index} title={tab.title} {...tab} key={tab.routeKey} />))}
  *
  * This component requires the childRoutes to be passed to it in the rendering component, example:
    * <NavTabs classNames="row organization uppercase" {...this.props} childRoutes={route.childRoutes} onHandleSelect={this.onTabSelect} />
 */

export default class NavTabs extends Component {
  static propTypes = {
    alwaysShowTabs: PropTypes.bool,
    childRoutes: PropTypes.array,
    classNames: PropTypes.string,
    confirmTabChange: PropTypes.func,
    onHandleSelect: PropTypes.func,
    route: PropTypes.shape({
      childRoutes: PropTypes.array
    }),
    router: PropTypes.object.isRequired
  };

  static defaultProps = {
    alwaysShowTabs: true
  };

  constructor(props) {
    super(props);
    this.state = {
      activeTab: this.getActiveTab()
    };
  }

  get childRoutes() {
    const { childRoutes, route } = this.props;
    return childRoutes || route.childRoutes || [];
  }

  /**
   * Check current active tab based on route
   */
  getActiveTab = () => {
    const { router } = this.props;
    const childRoutes = this.childRoutes;
    const currentPath = router ? router.location.pathname : "";
    const activeTab = childRoutes.find(child => currentPath.includes(child.path));

    if (!activeTab && childRoutes.length) {
      this.updateRoute(childRoutes[0].routeKey);
    }
    if (activeTab && typeof this.props.onHandleSelect === 'function') {
      this.props.onHandleSelect(activeTab.routeKey);
    }
    return activeTab || {};
  };

  findChildRoute = (routeKey, childRoutes) => {
    return childRoutes.find((child) => {
      return child.routeKey === routeKey;
    });
  }

  handleSelect = routeKey => {
    const childRoutes = this.childRoutes;
    const nextTab = this.findChildRoute(routeKey, childRoutes);
    const currentTab = childRoutes[this.state.activeTab?.keys];
    this.setState({ routeKey }, () => {
      if (typeof this.props.confirmTabChange === 'function') {
        this.props.confirmTabChange(nextTab, currentTab, this.changeTabs);
      } else {
        this.changeTabs();
      }
    })
  }

  updateRoute = routeKey => {
    const { router } = this.props;
    const childRoutes = this.childRoutes;
    let path = "";
    if (childRoutes.length) {
      const childRoute = this.findChildRoute(routeKey, childRoutes);
      path = PathHelper.ensureStartSlash(childRoute.path);
    }
    if (router) {
      router.replace(`${router.params.id || ''}${path}`);
    }
  }

  changeTabs = () => {
    const { routeKey } = this.state;
    const childRoutes = this.childRoutes;
    if (typeof this.props.onHandleSelect === 'function') {
      this.props.onHandleSelect(routeKey);
    }
    this.updateRoute(routeKey);
    const newTab = this.findChildRoute(routeKey, childRoutes);
    this.setState({ activeTab: newTab });
  }

  renderTabs() {
    const childRoutes = this.childRoutes;
    if (childRoutes) {
      return (
        <Tabs activeKey={this.state.activeTab?.routeKey} id={'tabsID'} onSelect={this.handleSelect}>
          {childRoutes.map((tab) => {
            if (!hasAccess({ ...this.props, ...tab })) {
              return null;
            }
            return (
              <Tab eventKey={tab.routeKey} title={tab.title} />
            );
          }).filter(Boolean)}
        </Tabs>
      )
    }
  }

  render() {
    const { alwaysShowTabs, classNames } = this.props;

    if (!alwaysShowTabs && this.childRoutes.length < 2) {
      return null;
    }

    const dataAttributes = pickDataAttributes(this.props);

    return (
      <nav className={`nav-tabs ${classNames}`} {...dataAttributes}>
        {this.renderTabs()}
      </nav>
    );
  }
}
